import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_RowData
} from "mantine-react-table-open";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDefaultMRTOptions } from "../../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../../mantine/getDefaultMRTRowEditModeOptions";
import type {
  Meter,
  MeterTableErrors,
  MeterWithSource
} from "../../../SiteSetupAssistant.types";
import { getIsCalibratedColumn } from "./Columns/getIsCalibratedColumn";
import { getLocationFeedinColumn } from "./Columns/getLocationFeedinColumn";
import { getLocationFeedoutColumn } from "./Columns/getLocationFeedoutColumn";
import { getMeterMeasurementTypeColumn } from "./Columns/getMeterMeasurementTypeColumn";
import { getMeterNumberColumn } from "./Columns/getMeterNumberColumn";
import { getNameColumn } from "./Columns/getNameColumn";
import { validateMeter } from "./validateMeter";

interface MeterTableProps {
  meterTableErrors: MeterTableErrors | null;
  meters: Array<MeterWithSource>;
  onBlockNavigation: (blocked: boolean) => void;
  onChange: (data: Array<MeterWithSource>) => void;
}

function MeterTable({
  meters,
  onBlockNavigation,
  onChange,
  meterTableErrors
}: MeterTableProps) {
  const [errors, setErrors] = useState<
    Partial<{ [k in keyof Meter]: string | null }>
  >({});

  const { t } = useTranslation();

  const handleRowValueChange = useCallback(
    (row: MRT_RowData) => {
      if (Object.keys(errors).length > 0) {
        const newErrors = validateMeter(row._valuesCache, t);
        if (Object.values(newErrors).some((error) => error !== null)) {
          setErrors(newErrors);
        } else {
          setErrors({});
        }
      }
    },
    [errors, t]
  );

  const getCellError = useCallback(
    (rowIndex: number, accessorKey: keyof MeterWithSource) => {
      if (
        meterTableErrors &&
        meterTableErrors[rowIndex] &&
        meterTableErrors[rowIndex][accessorKey]
      ) {
        return meterTableErrors[rowIndex][accessorKey];
      } else {
        return null;
      }
    },
    [meterTableErrors]
  );

  const columns = useMemo<Array<MRT_ColumnDef<MeterWithSource>>>(
    () => [
      getNameColumn(getCellError),
      getMeterMeasurementTypeColumn(getCellError),
      getIsCalibratedColumn(getCellError),
      getLocationFeedinColumn({
        errorText: errors.location_feedin,
        onChange: handleRowValueChange,
        getCellError
      }),
      getLocationFeedoutColumn({
        errorText: errors.location_feedout,
        onChange: handleRowValueChange,
        getCellError
      }),
      getMeterNumberColumn(getCellError)
    ],
    [errors, handleRowValueChange, getCellError]
  );

  const table = useMantineReactTable({
    ...getDefaultMRTOptions<MeterWithSource>({
      emptyRowsFallbackText:
        "Nach der Erstellung von Erzeugern und Verbrauchern in den vorherigen Schritten können hier die Zähler bearbeitet werden.",
      initialState: {
        pagination: {
          pageIndex: 0,
          pageSize: 15
        }
      },
      hideBorder: true
    }),
    ...getDefaultMRTRowEditModeOptions<MeterWithSource>({
      rowActions: {
        edit: {
          text: "Zähler bearbeiten",
          onClick: () => {
            onBlockNavigation(true);
          }
        }
      }
    }),
    columns,
    data: meters,
    enableFilters: false,
    mantineTopToolbarProps: {
      hidden: true
    },
    onEditingRowSave: ({ values, row }) => {
      const newErrors = validateMeter(values, t);
      if (Object.values(newErrors).some((error) => error !== null)) {
        setErrors(newErrors);
      } else {
        const newData = [
          ...meters.map((meter) => {
            if (meter.name === row.getValue("name")) {
              return { ...meter, ...values };
            } else {
              return meter;
            }
          })
        ];
        newData[row.index] = {
          ...values,
          source: meters[row.index].source,
          parentIndex: meters[row.index].parentIndex
        };
        onChange(newData);
        table.setEditingRow(null);
        setErrors({});
        onBlockNavigation(false);
      }
    },
    onEditingRowCancel: () => {
      setErrors({});
      onBlockNavigation(false);
    }
  });

  return (
    <div className="MeterTable">
      <MantineReactTable table={table} />
    </div>
  );
}

export { MeterTable, MeterTableProps };
